import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useSearchContext } from "domains/search/contexts/SearchProvider";
import { MeilisearchResult } from "domains/search/interfaces/Search";
import _ from "lodash";

export default function useTagAutocomplete(
  searchValue?: string,
  isEnabled = true
) {
  const { getAllIndexes, searchWithTokenRenewal } = useSearchContext();
  const { errorToast } = useScenarioToast();
  const [wasEnabled, setWasEnabled] = useState<boolean>(isEnabled);
  const [tags, setTags] = useState<string[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const debouncedSearchValue = useDebounce(searchValue);

  const privateIndex = useMemo(
    () => getAllIndexes("asset").private,
    [getAllIndexes]
  );

  const filteredTags = useMemo(
    () => tags?.filter((tag) => tag.startsWith(searchValue ?? "")),
    [tags, searchValue]
  );

  // ----------------------------------

  useEffect(() => {
    if (isEnabled && !wasEnabled) {
      setWasEnabled(true);
    }
  }, [isEnabled, wasEnabled]);

  useEffect(() => {
    if (!privateIndex) return;
    if (!searchWithTokenRenewal) return;
    if (!wasEnabled) return;

    const search = async () => {
      try {
        setIsLoading(true);
        setWasEnabled(true);

        const query = {
          indexName: privateIndex,
          params: {
            query: "",
            filters: _.compact([
              `tags IS NOT EMPTY`,
              debouncedSearchValue
                ? `tags STARTS WITH "${debouncedSearchValue}"`
                : undefined,
            ]).join(" AND "),
            facets: ["tags"],
            hitsPerPage: 0,
          },
        };

        const response = await searchWithTokenRenewal([query]);
        if (!response) return;
        const results: MeilisearchResult[] = response.results;
        setTags(Object.keys(results[0]?.facets.tags ?? {}));
      } catch (error) {
        setTags([]);
      } finally {
        setIsLoading(false);
      }
    };
    void search();
  }, [
    errorToast,
    privateIndex,
    searchWithTokenRenewal,
    debouncedSearchValue,
    setTags,
    wasEnabled,
  ]);

  // ----------------------------------

  return useMemo(
    () => ({
      tags: filteredTags,
      isLoading,
    }),
    [filteredTags, isLoading]
  );
}
